import { ReactNode } from "react";
import { AxiosResponse } from "axios";
import { ListThreadsRequest } from "duck/endpoints/threads";
import type { ClientAction } from "duck/ui/chat/actions";
import type { PriceAdvice } from "duck/ui/chat/components/messages/PriceAdvisorMessage/types";
import { ServiceAdvice } from "duck/ui/chat/components/messages/ServiceAdviceMessage/types";
import { TopLevelSpec } from "vega-lite";

import { DeleteRequest } from "shared/api/api";

import type {
  Message,
  MessageContent,
  MessageMetadata,
} from "./serverSentEvents/types";

export interface Thread {
  id: string;
  title: string | null;
  created_at: string;
  title_updated_at?: string | null;
  first_message_at?: string | null;
  last_message_at?: string | null;
}

export const SPAWN_SUBTHREAD_TOOL_NAME = "spawn_subthread";
export const INTERMEDIATE_OUTPUT_TOOL_NAME = "intermediate_output";

export const ChatMessageRole = {
  HUMAN: "human",
  AI: "ai",
  TOOL_CALL: "tool_call",
  TOOL_RESPONSE: "tool_response",
} as const;

export const RENDERABLE_MESSAGE_ROLES = [
  ChatMessageRole.HUMAN,
  ChatMessageRole.AI,
  ChatMessageRole.TOOL_CALL,
  ChatMessageRole.TOOL_RESPONSE,
] as const;

// Extract only chat message types from Message interface
export type ChatMessageRoleType = Extract<
  Message["type"],
  (typeof ChatMessageRole)[keyof typeof ChatMessageRole]
>;

export enum MessageType {
  MARKDOWN = "markdown",
  TABLE = "table",
  CHART = "chart",
  DOWNLOADABLE_CONTENT = "downloadable_content",
  SUGGESTED_REPAIRS = "suggested_repairs",
  WORKORDER_PRICING_VERIFICATION = "workorder_pricing_verification",
}

export interface BaseChatMessage {
  message_type: MessageType;
}

export interface MarkdownData extends BaseChatMessage {
  markdown_message: string;
}

export type DuckValueFormat =
  | "text"
  | "number"
  | "integer"
  | "ratio_percentage"
  | "boolean"
  | "date"
  | "date_with_time"
  | "year_month"
  | "year_month_day";

interface TableHeader {
  label: string;
  column_name: string | null;
  is_unique: boolean;
  column_width?: number | null;
  // Optional despite always being sent: tables stream in as JSON patches, so a header can arrive incomplete.
  value_format?: DuckValueFormat | null;
}

export interface TableData extends BaseChatMessage {
  title?: string;
  identifier?: string;
  main_database_table_name?: string | null;
  suitable_for_chart?: boolean;
  headers: TableHeader[] | string[];
  rows: string[][];
  // "card" was renamed to "key-value-display", still here for backward compatibility
  renderer?: "table" | "key-value-display" | "card";
}

export interface DownloadableContent extends BaseChatMessage {
  title: string;
  url: string;
  media_type: string;
}

export interface ServiceAdviceData extends ServiceAdvice, BaseChatMessage {}
export interface PriceAdviceData extends BaseChatMessage {
  workorder_pricing_verification: PriceAdvice;
}

export interface ChartData extends BaseChatMessage {
  chart: TopLevelSpec;
}

export interface BackQuackItem {
  status_update_message?: string | null;
  critical_error_message?: string | null;
}

export type AssistantMessageContent =
  | MarkdownData
  | TableData
  | ChartData
  | DownloadableContent
  | ServiceAdviceData
  | PriceAdviceData;

export interface AssistantMessage {
  messages: AssistantMessageContent[];
}

// Chat Context interfaces
export interface ChatContextType {
  threads: Thread[] | null;
  messages: ChatMessage[];
  isStreaming: boolean;
  isAnySubthreadStreaming: boolean;
  streamingSubthreadCount: number;
  isCreating: boolean;
  isSwitchingThread: boolean;
  isLoadingCurrentThread: boolean;
  currentThreadId: string | null;
  followupSuggestions: string[];
  isFetchingFollowups: boolean;
  followupQuestionsEnabled: boolean;
  setFollowupQuestionsEnabled: (enabled: boolean) => void;
  safeMode: boolean;
  setSafeMode: (enabled: boolean) => void;
  pendingClientActions: ClientAction[];
  setPendingClientActions: (actions: ClientAction[]) => void;
  sendMessage: (
    text: string,
    newThread?: boolean,
    attachments?: File[],
    safeModeOverride?: boolean
  ) => Promise<boolean>;
  stopStreaming: () => void;
  createNewThread: () => Promise<string>;
  refreshFollowupSuggestions: () => Promise<void>;
  selectThread: (threadId: string, refetchThreads: boolean) => void;
  deleteThread: (args: DeleteRequest) => Promise<AxiosResponse<any, any, {}>>;
  focusInput: () => void;
  inputRef: React.RefObject<HTMLElement | null>;
  setUseListThreadArgs: (args: ListThreadsRequest) => void;
  useListThreadArgs: ListThreadsRequest;
  isLoadingThreads: boolean;
}

export interface ChatProviderProps {
  children: ReactNode;
}

export interface ChatMessage {
  id: string;
  // Real server-assigned id, captured during streaming. The optimistic `id` is a
  // client UUID; feedback must target this once the server has acknowledged the message.
  serverId?: string;
  role: ChatMessageRoleType;
  content: MessageContent;
  state?: AssistantMessage;
  backQuack?: BackQuackItem;
  bytes?: number;
  startedAt?: number;
  endedAt?: number;
  aborted?: boolean;
  isOptimistic?: boolean;
  safeMode?: boolean;
  metadata?: MessageMetadata | null;
}
